import { ReactNode } from 'react';
import { useCurrentFrame, interpolate } from 'remotion';

// Constants
import { CLAMP_BOTH } from '@/constants/animations';
import { CURTAIN_ANIMATION, CLIP_PATHS } from './constants';

// Utils
import { createClipPathStyle, createBackfaceStyle } from '@/utils/styles';
import {
  calculateShadowOpacity,
  calculateContentPosition,
  calculateGeometry,
  calculateAnimationEndFrame,
  getColumnAnimationState,
} from './utils';

interface CurtainRevealAnimationProps {
  children: ReactNode; // Content to reveal
  columns?: number; // Number of curtain columns (default: 10)
  delay?: number; // Delay before animation starts in frames
  columnDuration?: number; // Duration for each column unfold (default: 24)
  staggerDelay?: number; // Stagger delay between columns (default: 3)
  showShadow?: boolean; // Show shadow overlay (default: true)
  shadowIntensity?: number; // Shadow intensity 0-1 (default: 0.3)
  className?: string; // Custom className
}

// Curtain unfold effect with columns scaling from left to right
export const CurtainRevealAnimation = ({
  children,
  columns = 10,
  delay = 0,
  columnDuration = 24,
  staggerDelay = 3,
  showShadow = true,
  shadowIntensity = 0.3,
  className = '',
}: CurtainRevealAnimationProps) => {
  const frame = useCurrentFrame();

  const { validColumns, columnWidthPercent, actualColumnWidth } =
    calculateGeometry(columns);

  const animationEndFrame = calculateAnimationEndFrame(
    delay,
    validColumns,
    staggerDelay,
    columnDuration,
  );

  // Fade in static layer during last frames
  const transitionProgress = interpolate(
    frame,
    [
      animationEndFrame - CURTAIN_ANIMATION.TRANSITION_FRAMES,
      animationEndFrame,
    ],
    [0, 1],
    CLAMP_BOTH,
  );

  // Static Layer (fades in on top to cover artifacts)
  const StaticLayer = (
    <div
      className={`absolute inset-0 ${className} z-10`}
      style={{
        overflow: 'visible',
        opacity: transitionProgress,
        visibility: transitionProgress > 0 ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>
  );

  if (frame >= animationEndFrame) return StaticLayer;

  const columnElements = Array.from({ length: validColumns }).map((_, i) => {
    // Calculate state for each column based on current frame
    const { scaleX, opacity, isVisible } = getColumnAnimationState(
      frame,
      delay + i * staggerDelay,
      columnDuration,
    );

    if (!isVisible) return null;

    // Calculate position for the inner content to counteract the column's transform
    const { widthPercent, leftPercent } = calculateContentPosition(
      i,
      columnWidthPercent,
      actualColumnWidth,
    );

    const shadowOpacity = calculateShadowOpacity(
      scaleX,
      showShadow,
      shadowIntensity,
    );

    return (
      <div
        key={i}
        className="absolute top-0 h-full"
        style={{
          left: `${i * columnWidthPercent}%`,
          width: `${actualColumnWidth}%`,
          transformOrigin: 'left center',
          transform: `scaleX(${scaleX})`,
          opacity,
          ...createClipPathStyle(
            i === 0 ? CLIP_PATHS.FIRST_COLUMN : CLIP_PATHS.DEFAULT_COLUMN,
          ),
          ...createBackfaceStyle(),
        }}
      >
        <div
          className="absolute top-0 h-full"
          style={{
            width: `${widthPercent}%`,
            left: `${leftPercent}%`,
          }}
        >
          {children}
        </div>

        {shadowOpacity > 0 && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(to right, rgba(0,0,0,${shadowOpacity}) 0%, transparent 50%)`,
            }}
          />
        )}
      </div>
    );
  });

  return (
    <>
      {/* Animated Columns Layer */}
      <div
        className={`absolute inset-0 ${className} z-0`}
        style={{ ...createClipPathStyle(CLIP_PATHS.CONTAINER), opacity: 1 }}
      >
        {columnElements}
      </div>

      {/* Static Layer (fades in to cover artifacts) */}
      {StaticLayer}
    </>
  );
};
